/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState, useEffect, useRef } from 'react';
import { ErrorMessage, Field, FieldProps, getIn, useFormikContext } from 'formik';
import { Alert, CheckboxInputAccessible } from '@components';
import { validateEmailAsync } from '@api/APIServices';
import { iReviewFormValues } from '@services/ReviewService';
import classnames from 'classnames';
import IssueIcon from '../../images/info.svg';
import { ReactComponent as EmailIcon } from '../../images/email.svg';
import styles from './EmailField.module.scss';

interface iProps extends FieldProps {
  title?: string;
  subtitle: string;
  isMandatory: boolean;
  placeholder: string;
  obligationNumber: string;
  formName: string;
  noEmailField: string;
  noEmailFieldRequired?: boolean;
  currentSP: string;
  warningMessage: string;
  displayError?: boolean;
}
const EmailFieldWithExperian = ({
  field,
  title,
  subtitle,
  isMandatory,
  currentSP,
  noEmailField,
  noEmailFieldRequired,
  placeholder = '',
  obligationNumber,
  formName,
  displayError,
  warningMessage = '',
}: iProps): ReactElement => {
  const [disableEmailInput, setEmailInput] = useState(false);
  const { errors, touched }: { errors: any; touched: any } = useFormikContext();
  // loading = true when the API call is running
  const [loading, setLoading] = useState(false);

  // API Call counter
  const [APICallCounter, setAPICallCounter] = useState(0);

  const [inputValue, setInputValue] = useState('');

  const hasError = getIn(errors, field.name);
  const touchedError = getIn(touched, field.name);

  const formikContext = useFormikContext<any>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isValidEmail = (email: string): boolean => {
    // eslint-disable-next-line no-control-regex
    const emailRegex = /^((([a-z]|\d|[!#$%&'*+\-/=?_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#$%&'*+\-/=\\?_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
    return emailRegex.test(email) && email.length < 101;
  };

  const callEmailAPI = async (currentCall: number) => {
    setLoading(true);
    await validateEmailAsync(inputValue, obligationNumber, formName).then((response) => {
      if (currentCall === APICallCounter) {
        if (!response) {
          formikContext.setFieldTouched(field.name, true);
        } else {
          formikContext.setFieldTouched(field.name, true);
        }
        setLoading(false);
        formikContext.setFieldValue(warningMessage, '');
        if (response !== '') {
          formikContext.setFieldValue(warningMessage, response);
        } else {
          formikContext.setFieldValue(warningMessage, '');
        }
      }
    });
    setLoading(false);
  };

  // calls the API 500 ms after user stops typing
  useEffect(() => {
    let currentCall = APICallCounter;
    let debounceTimer;
    setEmailInput(getIn(formikContext.values, noEmailField));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    timeoutRef.current = setTimeout(() => {
      if (isValidEmail(inputValue)) {
        debounceTimer = setTimeout(() => {
          callEmailAPI(currentCall);
        }, 500);
      } else {
        // formikContext.setFieldValue(warningMessage, '');
      }
    }, 500);
    return () => {
      currentCall = 0;
    };
  }, [inputValue, APICallCounter]);

  const handleChange = (e: any) => {
    formikContext.handleChange(e);
    setInputValue(e.target.value);
    setAPICallCounter((prevCount) => prevCount + 1);
  };

  return (
    <div
      className={classnames({
        [styles.container]: true,
        [styles.titlePresent]: title,
      })}>
      {title && (
        <div
          className={classnames({
            [styles.title]: true,
            [styles.titleError]: hasError && touchedError,
          })}>
          <span className={styles.titleBold}>{title}</span>
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      )}
      <div className={styles.inputContainer}>
        <EmailIcon className={styles.emailIcon} />
        <input
          type="text"
          className={classnames({
            [styles.formInput]: true,
            [styles.inputError]: hasError && touchedError,
            [styles.disabled]: disableEmailInput,
          })}
          required={isMandatory}
          placeholder={placeholder}
          {...field}
          value={field.value ? field.value : ''}
          onChange={handleChange}
          aria-label="Text Input"
          disabled={disableEmailInput}
        />
        {loading && (
          <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
            <div className={styles.cssloadwhirlpool} />
          </div>
        )}
      </div>
      {displayError && <ErrorMessage className={styles.errorMsg} name={field.name} component="span" />}
      {noEmailFieldRequired && (
        <Field
          name={noEmailField}
          component={CheckboxInputAccessible}
          title={currentSP}
          boxChecked={(e: boolean) => {
            setEmailInput(e);
            formikContext.setFieldTouched(field.name, false);
            formikContext.setFieldValue(field.name, '');
            formikContext.setFieldValue(warningMessage, '');
          }}
        />
      )}
      {formikContext.values[warningMessage] && !hasError && (
        <Field
          name={warningMessage}
          component={Alert}
          theme="yellowInfo"
          icon={IssueIcon}
          message={formikContext.values[warningMessage]}
        />
      )}
    </div>
  );
};

EmailFieldWithExperian.defaultProps = {
  title: 'Email',
  noEmailFieldRequired: true,
  displayError: true,
};

export default EmailFieldWithExperian;
